<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { getNonDelegableInactivatedReasons } from "@/modules/worker/helpers/inactivatedReasons";
import { useConfigurationStore } from "@/store/configurationStore";

import type { NonDelegableFlowResult } from "@/components/modal/Profile/nonDelegable/types";
import type { SelectOption } from "@/dsComponents/inputs/types";
import type { WorkerDelegabilityOptimisticPayload } from "@/modules/worker/helpers/workerDelegabilityOptimistic";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import NonDelegableFlowModals from "@/components/modal/Profile/nonDelegable/NonDelegableFlowModals.vue";

interface SwitchWorkerStatusProps {
    workerName?: string;
    openInactiveModal: boolean;
    workersId: string[];
    onOptimisticUpdate?: (payload: WorkerDelegabilityOptimisticPayload) => void;
    onOptimisticRollback?: () => void;
}

const props = defineProps<SwitchWorkerStatusProps>();

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();
const { toastSuccess } = useDefaultToast();
const configurationStore = useConfigurationStore();

const inactivatedReasonOptions = ref<{ label: string; value: string }[]>([]);
const inactivatedReasonsSelected = ref<{ label: string; value: string; comment: string }>({ label: "", value: "", comment: "" });
const isNonDelegableFlowOpen = ref(false);

const errors = ref({
    reason: false,
});

const fetchInactivatedReasons = async () => {
    inactivatedReasonOptions.value = getNonDelegableInactivatedReasons(configurationStore.configuration.inactivatedReasons).map((reason) => ({
        label: reason.reason,
        value: reason.id,
    }));
};

const handleSelectInactivatedOption = (option: SelectOption) => {
    errors.value.reason = false;
    inactivatedReasonsSelected.value.value = option.value ?? "";
};

const handleUpdateComplement = (value: string) => {
    inactivatedReasonsSelected.value.comment = value;
};

const handleOpenNonDelegableFlow = () => {
    if (!inactivatedReasonsSelected.value.value) {
        errors.value.reason = true;
        return;
    }

    if (Object.values(errors.value).some(Boolean)) {
        return;
    }

    isNonDelegableFlowOpen.value = true;
};

const handleNonDelegableFlowComplete = (result: NonDelegableFlowResult) => {
    if (result.action === "remove-from-agency") {
        toastSuccess(t("page.profile.removeFromAgency.success"));
    } else {
        toastSuccess(t("page.worker.profile.deactivate.success"));
    }
    emit("close");
};

const handleNonDelegableFlowClose = () => {
    isNonDelegableFlowOpen.value = false;
};

onMounted(() => {
    fetchInactivatedReasons();
});
</script>
<template>
    <Modal
        width="600px"
        @close="() => emit('close')"
        open
        :title="t('page.profile.actions.deactivate', { count: props.workersId.length })"
        centerHeader
        close
    >
        <div class="modal-content">
            <Select
                :label="t('global.motif')"
                required
                :options="inactivatedReasonOptions"
                v-model="inactivatedReasonsSelected.label"
                @select="handleSelectInactivatedOption"
                width="100%"
                :placeholder="t('modals.inactiveInterim.placeholder.reason')"
                :error="errors.reason ? t('errors.required') : undefined"
            />
            <TextareaInput
                :rows="4"
                :label="t('global.comment')"
                :modelValue="inactivatedReasonsSelected.comment"
                @update:modelValue="handleUpdateComplement"
                :placeholder="t('modals.inactiveInterim.placeholder.complement')"
                :showErrorText="false"
            />
        </div>
        <template #footer>
            <div class="button-container">
                <ButtonAlt type="neutral" :label="t('global.actions.cancel')" @click="() => emit('close')" />
                <ButtonAlt
                    type="error"
                    variant="filled"
                    :label="t('page.profile.actions.deactivate', { count: props.workersId.length })"
                    @click="handleOpenNonDelegableFlow"
                />
            </div>
        </template>
    </Modal>

    <NonDelegableFlowModals
        :open="isNonDelegableFlowOpen"
        :worker-ids="props.workersId"
        :worker-name="props.workerName"
        :reason-id="inactivatedReasonsSelected.value"
        :comment="inactivatedReasonsSelected.comment"
        :on-optimistic-update="props.onOptimisticUpdate"
        :on-optimistic-rollback="props.onOptimisticRollback"
        @close="handleNonDelegableFlowClose"
        @complete="handleNonDelegableFlowComplete"
    />
</template>
<style scoped lang="scss">
.modal-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3xl, 24px);
    align-self: stretch;
}
.button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--spacing-lg, 12px);
    flex: 1 0 0;
}
</style>
